import {PageData} from "@app/core-app/app/usecase/notion/fetch-pages";
import { OutboxDbConfig} from "@app/core-app/app/data/profile";
import {
  Box,
  Button, CircularProgress, Flex,
  HStack,
  Stack, Switch,
  Text, Tooltip,
  VStack
} from "@chakra-ui/react";
import {CheckIcon, RefreshIcon, XIcon, AtSymbolIcon, UserIcon} from "@heroicons/react/outline";
import {useFormik} from "formik";
import React, {ChangeEvent, useState} from "react";
import {shouldDeliverPage} from "@app/core-app/app/domain/notion/properties";
import {EmailLogEntry} from "@app/core-app/app/data/email-log-entry";
import {OutboxStatsModal} from "./OutboxStatsTable";
import {event} from "../../../services/analytics";
import {EditRenderPreferencesModal} from "../organisms/EditRenderPreferencesModal";
import {InfoOutlineIcon} from "@chakra-ui/icons";
import EditableInput from "../atoms/EditableInput";
import {ProfileService} from "@app/core-app/app/service/profile-service";
import {DatabaseInfo} from "@app/core-app/app/data/notion/DatabaseInfo";
import {UserProfileData} from "@app/core-app/app/usecase/fetch-user-profile";
import {ConnectRecipientsDatabase} from "../organisms/ConnectRecipientsDatabase";
import {IconLink} from "../atoms/IconLink";
import {PropertyStatusIndicator} from "../organisms/DatabaseCard/PropertyStatusIndicator";
import {CollapsibleSection} from "../atoms/CollapsibleSection";
import {DropdownSelector} from "../atoms/DropdownSelector";
import {CardRow} from "../organisms/DatabaseCard/CardRow";
import {OutboxPropertyConfiguration} from "@app/core-app/app/domain/notion/configurations/outbox-props";
import { OutboxPagesModal } from "../organisms/OutboxPagesModal";


const OutboxConfiguration = ({onChange, value}: {
  onChange: (config: OutboxDbConfig) => void,
  value: OutboxDbConfig
}) => {
  const formik = useFormik({
    initialValues: value,
    onSubmit: (values) => {
      if (onChange) {
        onChange(values)
      }
    },
  })

  const onChangeWrapped = (e: string | ChangeEvent) => {
    formik.handleChange(e)
    formik.handleSubmit()
  }

  return <Stack direction={["column", "column", "row"]} alignItems={["start", "start", "center"]} spacing={"4"}>
    <VStack alignItems={"start"} spacing={"0"} maxW={"md"}>
      <Text textColor={"black"} fontWeight={"medium"}>Automatically send emails</Text>
      <Text textColor={"pink.500"} fontSize={"sm"}>Premium feature</Text>
      <Text textColor={"gray.700"}>Send emails automatically when marked as Ready to Send (emails are sent once in
        a few
        minutes)</Text>
    </VStack>
    <form onSubmit={formik.handleSubmit}>
      <Switch colorScheme={"blue"} id='autoSend' onChange={onChangeWrapped}
        isChecked={formik.values?.autoSend ?? false}/>

    </form>

  </Stack>
}


const ConnectedDatabaseConfiguration = ({databaseId, profile, onAutoconfig, onDisconnect}: {
  databaseId: string,
  profile: UserProfileData,
  onAutoconfig: () => Promise<any>,
  onDisconnect: () => Promise<any>
}) => {
  const [loading, setLoading] = useState(false)
  const database = profile.databases.find((db) => db.id === databaseId)
  if (!database) {
    return <Text>Error: database is not found.</Text>
  }

  return <>
    <Text>Connected recipient database is used as list of recipients for all emails. <br/>
      When connected, overwrites any recipient properties in the email pages.
    </Text>
    {database.recipientsConfig.result === "error" &&
      <Button colorScheme={"blue"} variant={"solid"} size={"sm"} onClick={
        () => {
          setLoading(true)
          onAutoconfig()
            .finally(() => setLoading(false))
        }}
      isLoading={loading}><span>Autoconfigure properties</span></Button>
    }

    <PropertyStatusIndicator title={"Email"}
      description={`Create property "Email" with email type. This address is used to deliver the outbox email.`}
      isOk={database.recipientsConfig.email.ok}/>
    <PropertyStatusIndicator title={"Subscribed"}
      description={`Create property "Subscribed" with checkbox type. This property is used to indicate that user should receive email.`}
      isOk={database.recipientsConfig.subscription.ok}/>

    <Button
      colorScheme={"red"}
      variant={"outline"}
      size={"sm"}
      w={"fit-content"}
      isLoading={loading}
      onClick={() => {
        setLoading(true)
        onDisconnect()
          .finally(() => setLoading(false))
      }}>Disconnect database</Button>
  </>

}
export type SendEmailsTabProps = {
  database: DatabaseInfo
  profile: UserProfileData
  pages: PageData[]
  outboxConfig: OutboxDbConfig
  outboxLog: EmailLogEntry[] | undefined | null
  isOutboxLoading: boolean
  outboxProperties: OutboxPropertyConfiguration[]
  isLoadingPages: boolean

  onRevalidate: () => any
  onPageSend: (page: PageData) => Promise<any>
  onPagePreview: (page: PageData) => void

  onAutoconfigure: () => void
  onRecipientsAutoconfigure: () => Promise<any>
  onConfigurationChange: (config: OutboxDbConfig) => Promise<any>
  onCreateNewEmail: () => Promise<any>

}
export const SendEmailsTab = ({
  profile,
  database,
  pages,
  isLoadingPages,
  isOutboxLoading,
  outboxProperties,
  outboxLog,
  onPageSend,
  onRevalidate,
  onPagePreview,
  outboxConfig,
  onAutoconfigure,
  onRecipientsAutoconfigure,
  onConfigurationChange,
  onCreateNewEmail
}: SendEmailsTabProps) => {


  const configured = outboxProperties.filter(x => !x.optional).every(p => p.property)
  const [progressing, setProgressing] = useState(false)

  const recipientDb = outboxConfig.recipientsDatabaseId
  const recipientDbObject = recipientDb ? profile.databases.find(db => db.id === recipientDb) : null


  return <VStack alignItems={"start"} w={"full"} spacing={"8"} paddingY={"2"}>
    <VStack alignItems={"start"} w={"full"} spacing={"4"}>
      <Flex direction={"column"} alignItems={"start"}>
        <HStack alignItems={"start"} spacing={"4"} justifyContent={"center"}>
          <Text textColor={"black"} fontWeight={"medium"}>Ready to send emails</Text>
          {isLoadingPages && <CircularProgress size={5} isIndeterminate color='blue.500'/>}
          {!isLoadingPages &&
            <Box color="gray.600"
              onClick={() => {
                if (onRevalidate) {
                  event({action: "refresh_outbox_click", params: {}})
                  onRevalidate()
                }
              }} h={5} w={5}>
              <RefreshIcon strokeWidth={1.25}/>
            </Box>}
        </HStack>
        {!pages?.length &&
          <Text textColor={"gray.700"} my={1} maxW={"md"}>No pages to send.
            Pages with recipients is displayed here if marked as ready to be sent and not yet sent.</Text>}

      </Flex>

      {pages && pages.map((page, index) => {
        const recipientsExists = page?.recipients?.length
        let recipients = null
        if (page.recipients && recipientsExists) {
          if (recipientsExists > 2) {
            recipients = `${page.recipients[0]} and ${page.recipients.length - 1} more`
          } else {
            recipients = page.recipients.join(", ")
          }
        }

        const hasToBeDelivered = shouldDeliverPage(page)

        return <Stack key={page.id} direction={["column", "column", "row"]}
          alignItems={["start", "start", "center"]} spacing={"4"}>
          <VStack alignItems={"start"} spacing={"0"} maxW={"md"} minW={"sm"}>
            <Text textColor={"black"} fontWeight={"medium"}>{page.title}</Text>
            {page.sendAt && <Text textColor={"gray.700"} fontSize={"sm"}>Send
              at: {page.sendAt} | {hasToBeDelivered ? "Ready" : "Waiting"}</Text>}

            <Flex w={'full'} h={"full"}
              justifyContent={"start"}
            >

              <Tooltip label={page.recipients?.join(" ")} fontSize={"xs"}>
                <Text textColor={"gray.700"} fontSize={"sm"} verticalAlign={"center"}>
                  {recipients ? `Recipients: ${recipients}` : `No recipient(s)`}
                  <InfoOutlineIcon mx={1} mb={0.5} fontSize={"xs"}/>

                </Text>
              </Tooltip>
            </Flex>
          </VStack>
          {page.url &&
            <IconLink url={page.url}/>
          }
          <Button
            isLoading={progressing}
            variant={"outline"}
            colorScheme={"blue"}
            size={"sm"}
            onClick={() => {
              if (onPageSend) {
                event({action: "send_page_manually_click", params: {title: page.title, id: page.id}})
                setProgressing(true)
                onPageSend(page)
                  .then(() => setProgressing(false))
              }
            }}
          >
            <span> Send </span>
          </Button>

          <Button
            variant={"link"}
            colorScheme={"blue"}
            size={"sm"}
            onClick={() => {
              if (onPagePreview) {
                event({action: "preview_page_click", params: {title: page.title, id: page.id}})
                onPagePreview(page)
              }
            }}>
            <span>Preview</span>
          </Button>


        </Stack>
      })}


      <HStack alignItems={"start"} justifyContent={"center"}>
        {database.outboxConfig.type === "v2" && database.outboxConfig.report.readyToSend.ok && <Button
          isLoading={progressing}
          variant={"outline"}
          colorScheme={"blue"}
          onClick={() => {
            if (onCreateNewEmail) {
              event({action: "create_new_email_click", params: {}})
              setProgressing(true)
              onCreateNewEmail()
                .then(() => {
                  setProgressing(false)

                })
            }
          }}
          size={"sm"}>Create new email</Button>
        }
        {database.outboxConfig.type === "v2" &&
          database.outboxConfig.report.result === "ok" &&
          <OutboxPagesModal onPagePreview={onPagePreview} onPageSend={onPageSend} databaseId={database.id}/>
        }
      </HStack>


    </VStack>

    <OutboxConfiguration
      value={outboxConfig}
      onChange={(config) => {
        if (onConfigurationChange) {
          onConfigurationChange(config)
        }
      }}/>

    <CardRow
      left={() => {
        return <>
          <Text textColor={"black"} fontWeight={"medium"}>Configure email formatting</Text>
          <Text textColor={"gray.700"}>Adjust how emails are created and formatted from your Notion
            page. </Text>
        </>
      }}

      right={() => {
        return <EditRenderPreferencesModal
          renderPreferences={ProfileService.findOutboxRenderPreferences({profile: profile.profile, databaseId: database.id})}
          onSave={(preferences) => {
            if (preferences) {
              onConfigurationChange({...outboxConfig, renderConfiguration: preferences})
            }
          }}
        />
      }}
    />

    <CardRow
      left={() => {
        return <>
          <Text textColor={"black"} fontWeight={"medium"} mb={1}>Email Statistics</Text>
          <Text textColor={"gray.700"}>Statistics and detailed information about sent emails.</Text>
        </>
      }}

      right={() => {
        return <OutboxStatsModal stats={outboxLog} loading={isOutboxLoading}/>

      }}
    />

    <CardRow
      left={() =>
        <>
          <Text textColor={"black"} fontWeight={"medium"}>Recipients Configuration</Text>
          <Text textColor={"gray.700"}>Connect a list of recipients as Notion database. Each row of the
            database will indicate subscribed recipients. </Text>
        </>
      }
      right={() => {
        return <>
          {!outboxConfig.recipientsDatabaseId &&
            <ConnectRecipientsDatabase databases={profile.databases}
              onDatabaseSelected={(database) => {
                return onConfigurationChange({
                  ...outboxConfig,
                  recipientsDatabaseId: database.id
                })

              }}/>
          }
          {outboxConfig.recipientsDatabaseId &&
            <Stack>
              <Stack direction={"row"} fontSize={"md"} alignItems={"center"} spacing={2}>
                <Text>Connected to:</Text>
                <Text fontWeight={"medium"}>
                  {recipientDbObject?.name}
                </Text>

                {recipientDbObject?.url && <IconLink url={recipientDbObject?.url} boxSize={4}/>}

              </Stack>
            </Stack>}
        </>
      }
      }
    />

    {outboxConfig.recipientsDatabaseId &&
      <CollapsibleSection
        title={() => <HStack spacing={2}>
          <UserIcon className={"h-4 w-4 inline rounded-xl"}/>
          <Text>Recipients Database Configuration</Text>
        </HStack>
        }>
        <ConnectedDatabaseConfiguration
          databaseId={outboxConfig.recipientsDatabaseId}
          profile={profile}
          onDisconnect={() => {
            return onConfigurationChange({...outboxConfig, recipientsDatabaseId: null})
          }}
          onAutoconfig={onRecipientsAutoconfigure}/>
      </CollapsibleSection>
    }

    <CollapsibleSection
      title={() =>
        <HStack spacing={2}>
          <AtSymbolIcon className={"h-4 w-4 inline rounded-xl"}/>
          <Text>Email Address Configuration</Text>
        </HStack>}
    >

      <CardRow
        left={() => {
          return <>
            <Text textColor={"black"} fontWeight={"medium"}>From Address</Text>
            <Text textColor={"gray.700"}>Email address that will appear as FROM in your emails. Must be at
              verified
              domain or added as
              sender.</Text>
          </>
        }}

        right={() => {
          return <EditableInput
            value={ProfileService.getFromEmailAddress({profile: profile.profile, databaseId: database.id})}
            onSave={(newEmail) => {
              if (newEmail) {
                onConfigurationChange({...outboxConfig, fromEmailAddress: newEmail})
              }
            }}/>
        }}
      />

      <CardRow
        left={() => {
          return <>
            <Text textColor={"black"} fontWeight={"medium"}>From Sender Name</Text>
            <Text textColor={"gray.700"}>
              Sender name that will appear as FROM in your emails. (In addition to email address).
            </Text>
          </>
        }}

        right={() => {
          return <EditableInput value={outboxConfig.fromSenderName ?? ""}
            onSave={(newName) => {
              if (newName) {
                onConfigurationChange({...outboxConfig, fromSenderName: newName})

              }
            }}/>

        }}
      />
      <CardRow
        left={() =>
          <>
            <Text textColor={"black"} fontWeight={"medium"}>Reply-to Address</Text>
            <Text textColor={"gray.700"}>Email address that will appear in Reply-to in your sent
              emails.</Text>
          </>
        }
        right={() => {
          return <EditableInput value={outboxConfig.replyToEmailAddress ?? ""}
            onSave={(newEmail) => {
              if (newEmail) {
                onConfigurationChange({
                  ...outboxConfig,
                  replyToEmailAddress: newEmail
                })
              }
            }}/>
        }
        }
      />

    </CollapsibleSection>

    <CollapsibleSection
      title={() => {
        return <>
          {configured ? <CheckIcon
            className={"h-4 w-4 inline text-white bg-green-700 opacity-80 rounded-xl"}/> :
            <XIcon
              className={"h-4 w-4 p-0.5 inline text-white bg-red-700 opacity-80 rounded-xl"}/>}
          <Text>Properties Configuration</Text>
        </>
      }}
    >
      {!configured &&
        <Button
          variant={"solid"}
          colorScheme={"blue"} size={"sm"}
          onClick={() => onAutoconfigure()}
        >
          <span>Autoconfigure default properties</span>
        </Button>
      }

      {outboxProperties.map(({property, name, description, modify, selectedProperty}, index) => {
        return <HStack key={index} my={1} alignItems={"start"} spacing={2}>
          <Box>
            {property ?
              <CheckIcon
                className={"h-4 w-4 inline text-white bg-green-700 opacity-80 rounded-xl"}/> :
              <XIcon
                className={"h-4 w-4 p-0.5 inline text-white bg-red-700 opacity-80 rounded-xl"}/>}
          </Box>
          <VStack alignItems={"start"} spacing={"0"}>
            <Text textColor={"black"} fontWeight={"medium"}>{name}</Text>
            <Text textColor={"gray.700"} maxW={"2xl"}>{description}</Text>
            {/*<Text>Selected property: {selectedProperty || ""} / {property}</Text>*/}
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <Text>Select property name:</Text>
              <Flex w={"200px"} fontSize={"sm"}>
                <DropdownSelector
                  value={property || selectedProperty}
                  options={database.properties}
                  onChange={(dataObject) => {
                    if (modify && dataObject) {
                      const updated = modify(outboxConfig.customFieldNames, dataObject.value)
                      onConfigurationChange({...outboxConfig, customFieldNames: updated})
                    }


                  }}/>
              </Flex>
            </Stack>
          </VStack>
        </HStack>
      })}
    </CollapsibleSection>


  </VStack>

}
